import { ArrowLeft, ChevronRight, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/cn";

import communityMarket from "@/assets/stokkie/community-market.webp";
import communityMarketSm from "@/assets/stokkie/community-market-sm.webp";
import fruitMarket from "@/assets/stokkie/fruit-market.webp";
import fruitMarketSm from "@/assets/stokkie/fruit-market-sm.webp";
import handsTogether from "@/assets/stokkie/hands-together.webp";
import handsTogetherSm from "@/assets/stokkie/hands-together-sm.webp";
import togetherAtTheStall from "@/assets/stokkie/together-at-the-stall.webp";
import togetherAtTheStallSm from "@/assets/stokkie/together-at-the-stall-sm.webp";
import vegetableCrates from "@/assets/stokkie/vegetable-crates.webp";
import vegetableCratesSm from "@/assets/stokkie/vegetable-crates-sm.webp";

/**
 * Stokkie Stokvel's look, in one place: photographs, big tiles, and the drawn
 * infographics that say with a picture what a paragraph would bury.
 *
 * Designed for somebody who does not use apps much. One idea per card, an icon
 * *and* a plain word on everything that can be tapped, targets a thumb cannot
 * miss, and numbers drawn as pictures — "8 of 12 paid" as a ring filling up.
 */

// ─── Photographs ──────────────────────────────────────────────────────────────
const PHOTOS = {
  stall: { large: togetherAtTheStall, small: togetherAtTheStallSm },
  hands: { large: handsTogether, small: handsTogetherSm },
  fruit: { large: fruitMarket, small: fruitMarketSm },
  vegetables: { large: vegetableCrates, small: vegetableCratesSm },
  market: { large: communityMarket, small: communityMarketSm },
} as const;

export type PhotoName = keyof typeof PHOTOS;

/**
 * A photograph at two sizes: a phone downloads the 640-pixel one (25–60 KB),
 * a wide screen the 1280. Lazy unless it is the first thing on the page.
 */
export function Photo({
  name,
  alt,
  className,
  eager,
}: {
  name: PhotoName;
  alt: string;
  className?: string;
  eager?: boolean;
}) {
  const photo = PHOTOS[name];
  return (
    <img
      src={photo.small}
      srcSet={`${photo.small} 640w, ${photo.large} 1280w`}
      sizes="(min-width: 1024px) 50vw, 100vw"
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      className={cn("object-cover", className)}
    />
  );
}

// ─── Tiles ────────────────────────────────────────────────────────────────────
type Tone = "teal" | "gold" | "green" | "navy";

const TONES: Record<Tone, string> = {
  teal: "bg-brand-500 text-white",
  gold: "bg-gold-500 text-navy-800",
  green: "bg-money-500 text-white",
  navy: "bg-navy-800 text-white",
};

/**
 * One big thing to tap: a coloured icon, one or two words, and — optionally —
 * a short line under it and a number that wants attention.
 */
export function BigTile({
  to,
  icon: Icon,
  label,
  hint,
  tone = "teal",
  badge,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  hint?: string;
  tone?: Tone;
  badge?: number | string | undefined;
}) {
  return (
    <Link
      to={to}
      className="relative flex min-h-[7.5rem] flex-col justify-between gap-3 rounded-2xl bg-surface p-4 shadow-card ring-1 ring-line transition hover:shadow-pop focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 active:scale-[0.98]"
    >
      <span
        className={cn(
          "grid size-12 place-items-center rounded-2xl",
          TONES[tone],
        )}
      >
        <Icon className="size-6" aria-hidden />
      </span>
      <span>
        <span className="block text-base font-bold leading-tight text-ink-900">
          {label}
        </span>
        {hint && (
          <span className="mt-0.5 block text-sm leading-snug text-ink-500">
            {hint}
          </span>
        )}
      </span>
      {badge !== undefined && badge !== 0 && (
        <span className="absolute right-3 top-3 grid min-w-7 place-items-center rounded-full bg-danger-500 px-2 py-0.5 text-sm font-bold text-white">
          {badge}
        </span>
      )}
    </Link>
  );
}

/**
 * A grid of tiles: two across on any phone, three or four on a wider screen.
 * Two across even on the smallest phone, on purpose — like a keypad, the tiles
 * are sized for a thumb, and one column made the hub a long scroll.
 */
export function TileGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{children}</div>
  );
}

/** A wide card that says one thing and leads somewhere: "2 people want to join". */
export function ActionBanner({
  to,
  icon: Icon,
  title,
  text,
  tone = "gold",
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  text?: string;
  tone?: Tone;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-2xl bg-surface p-4 shadow-card ring-1 ring-line hover:shadow-pop"
    >
      <span
        className={cn(
          "grid size-12 shrink-0 place-items-center rounded-2xl",
          TONES[tone],
        )}
      >
        <Icon className="size-6" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold text-ink-900">{title}</span>
        {text && <span className="block text-sm text-ink-500">{text}</span>}
      </span>
      <ChevronRight className="size-6 shrink-0 text-ink-400" aria-hidden />
    </Link>
  );
}

// ─── Page header ──────────────────────────────────────────────────────────────
/** A big back arrow and a big title: where am I, and how do I get back. */
export function SectionHeader({
  back,
  backLabel,
  title,
  subtitle,
}: {
  back: string;
  backLabel: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="space-y-2">
      <Link
        to={back}
        className="inline-flex h-11 items-center gap-2 rounded-full bg-surface pl-3 pr-4 text-sm font-semibold text-ink-600 shadow-card ring-1 ring-line"
      >
        <ArrowLeft className="size-5" aria-hidden />
        {backLabel}
      </Link>
      <h1 className="text-2xl font-extrabold leading-tight text-ink-900">
        {title}
      </h1>
      {subtitle && <p className="text-ink-500">{subtitle}</p>}
    </div>
  );
}

// ─── Infographics ─────────────────────────────────────────────────────────────
/**
 * "8 of 12 paid", as a ring that fills up. Read at a glance by somebody who
 * would not read the sentence.
 */
export function PaidRing({
  paid,
  total,
  size = 132,
  caption = "paid",
}: {
  paid: number;
  total: number;
  size?: number;
  caption?: string;
}) {
  const stroke = Math.round(size / 9);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const share = total > 0 ? Math.min(1, paid / total) : 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${paid} of ${total} ${caption}`}
      className="shrink-0"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        className="stroke-brand-50"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${circumference * share} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className={share >= 1 ? "stroke-money-500" : "stroke-brand-500"}
      />
      <text
        x="50%"
        y="47%"
        textAnchor="middle"
        className="fill-ink-900"
        style={{ fontSize: size / 4.2, fontWeight: 800 }}
      >
        {paid}/{total}
      </text>
      <text
        x="50%"
        y="66%"
        textAnchor="middle"
        className="fill-ink-500"
        style={{ fontSize: size / 10, fontWeight: 600 }}
      >
        {caption}
      </text>
    </svg>
  );
}

/**
 * Buying together, drawn: the more the group orders, the lower each pack
 * costs. Bars for each rung of the wholesaler's ladder, the reached one
 * highlighted.
 */
export function PriceLadder({
  rungs,
  reached,
}: {
  rungs: { label: string; price: number }[];
  reached: number;
}) {
  // Scaled between the cheapest and the dearest rung, not from zero: R220
  // against R190 drawn from zero is two bars the same height, and the saving —
  // the whole point of the picture — disappears.
  const top = Math.max(...rungs.map((r) => r.price));
  const bottom = Math.min(...rungs.map((r) => r.price));
  const height = (price: number) =>
    top === bottom
      ? 120
      : Math.round(48 + ((price - bottom) / (top - bottom)) * 84);
  return (
    <div
      className="flex items-end justify-around gap-3"
      role="img"
      aria-label="Price per pack falls as the group orders more"
    >
      {rungs.map((rung, index) => {
        const active = index === reached;
        return (
          <div
            key={rung.label}
            className="flex flex-1 flex-col items-center gap-2"
          >
            <span
              className={cn(
                "text-sm font-bold",
                active ? "text-money-600" : "text-ink-500",
              )}
            >
              R{rung.price}
            </span>
            <div
              className={cn(
                "w-full max-w-16 rounded-t-xl",
                active ? "bg-money-500" : "bg-brand-100",
              )}
              style={{ height: `${height(rung.price)}px` }}
            />
            <span className="text-center text-xs font-medium text-ink-600">
              {rung.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** How it works, as numbered circles joined by a line. */
export function StepFlow({
  steps,
}: {
  steps: { icon: LucideIcon; title: string; text: string }[];
}) {
  return (
    <ol className="relative grid gap-6 md:grid-cols-4">
      {steps.map((step, index) => (
        <li
          key={step.title}
          className="relative flex gap-4 md:flex-col md:items-center md:text-center"
        >
          {index < steps.length - 1 && (
            <span
              aria-hidden
              className="absolute left-7 top-14 h-[calc(100%-1.5rem)] w-1 rounded bg-brand-100 md:left-[calc(50%+2rem)] md:top-7 md:h-1 md:w-[calc(100%-4rem)]"
            />
          )}
          <span className="relative z-10 grid size-14 shrink-0 place-items-center rounded-full bg-brand-500 text-white shadow-card ring-4 ring-canvas">
            <step.icon className="size-7" aria-hidden />
            <span className="absolute -right-1 -top-1 grid size-6 place-items-center rounded-full bg-gold-500 text-xs font-extrabold text-navy-800">
              {index + 1}
            </span>
          </span>
          <span>
            <span className="block text-lg font-bold text-ink-900">
              {step.title}
            </span>
            <span className="mt-1 block text-ink-600">{step.text}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}
